import { useEffect } from "react";
import { Link } from "react-router-dom";
import { LOGO_URL, COMPANY } from "@/data/site";
import { motion } from "framer-motion";

const SECTIONS = [
  {
    title: "1. Titolare del Trattamento",
    body: `Il Titolare del trattamento dei dati personali è ${COMPANY.legal}, con sede legale in ${COMPANY.address}, P.IVA ${COMPANY.piva}, titolare del marchio ${COMPANY.brand}. Per qualsiasi informazione relativa alla privacy puoi contattarci all'indirizzo email ${COMPANY.email} o al numero verde ${COMPANY.phone}.`,
  },
  {
    title: "2. Dati che raccogliamo",
    body: "Raccogliamo i dati che ci fornisci volontariamente in fase di registrazione, prenotazione di un piano di pulizia, iscrizione alla waitlist dell'app o invio di una richiesta di contatto: nome e cognome, indirizzo email, numero di telefono, indirizzo dell'impianto fotovoltaico, città, potenza dell'impianto (kW), data preferita di intervento ed eventuali note sull'accessibilità del tetto. Raccogliamo inoltre dati tecnici di navigazione anonimi necessari al funzionamento del sito.",
  },
  {
    title: "3. Finalità e base giuridica del trattamento",
    body: "I tuoi dati sono trattati per: (a) gestire la registrazione al sito e la tua area riservata (esecuzione di un contratto, art. 6.1.b GDPR); (b) gestire le prenotazioni dei servizi di pulizia, inclusa la comunicazione della data di intervento (esecuzione di un contratto); (c) rispondere alle richieste di preventivo e assistenza (legittimo interesse, art. 6.1.f GDPR); (d) inviarti, solo se ti iscrivi alla waitlist, comunicazioni sul lancio dell'app AirDroneJet (consenso, art. 6.1.a GDPR); (e) adempiere agli obblighi di legge fiscali e amministrativi (art. 6.1.c GDPR).",
  },
  {
    title: "4. Modalità del trattamento e sicurezza",
    body: "Il trattamento avviene con strumenti informatici e misure di sicurezza tecniche e organizzative adeguate (cifratura delle password, accessi protetti, comunicazioni cifrate in HTTPS). I dati non sono soggetti a processi decisionali automatizzati né a profilazione.",
  },
  {
    title: "5. Comunicazione a terzi",
    body: "I tuoi dati NON sono venduti né ceduti a terzi per finalità di marketing. Possono essere comunicati esclusivamente a: fornitori tecnici che ospitano e mantengono questa piattaforma (nominati Responsabili del trattamento ex art. 28 GDPR), consulenti fiscali e contabili per obblighi di legge, e autorità competenti quando richiesto dalla normativa vigente.",
  },
  {
    title: "6. Conservazione dei dati",
    body: "I dati di account e prenotazioni sono conservati per tutta la durata del rapporto contrattuale e per i successivi 10 anni per obblighi fiscali. I dati della waitlist sono conservati fino al lancio dell'app o fino a revoca del consenso. I messaggi di contatto sono conservati per 24 mesi.",
  },
  {
    title: "7. I tuoi diritti (artt. 15-22 GDPR)",
    body: "In qualunque momento puoi esercitare i diritti di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione al trattamento, nonché revocare il consenso prestato, scrivendo a " + COMPANY.email + ". Hai inoltre il diritto di proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).",
  },
  {
    title: "8. Cookie",
    body: "Questo sito utilizza esclusivamente cookie tecnici necessari al funzionamento dell'autenticazione e della sessione. Non utilizziamo cookie di profilazione di terze parti per finalità pubblicitarie.",
  },
  {
    title: "9. Modifiche a questa informativa",
    body: "La presente informativa può essere aggiornata nel tempo. La versione vigente è sempre disponibile a questa pagina. Ultimo aggiornamento: ottobre 2026.",
  },
];

export default function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#050507] min-h-screen" data-testid="privacy-page">
      <header className="glass border-b border-white/10">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="privacy-logo-link">
            <img src={LOGO_URL} alt="AirDroneJet" className="h-9 w-auto" />
            <span className="font-heading font-extrabold tracking-tight">
              AIR<span className="fire-text">DRONE</span>JET
            </span>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-home-link"
            className="font-mono-adj text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-orange-400 transition-colors"
          >
            ← Torna alla Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <p className="font-mono-adj text-xs uppercase tracking-[0.3em] text-orange-500 font-semibold">Documento Legale</p>
          <h1 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight text-white mt-4">
            Privacy <span className="fire-text">Policy</span>
          </h1>
          <p className="text-base text-slate-400 leading-relaxed mt-5 max-w-2xl">
            Informativa sul trattamento dei dati personali ai sensi del Regolamento (UE) 2016/679
            ("GDPR") e del D.Lgs. 196/2003 come modificato dal D.Lgs. 101/2018.
          </p>
        </motion.div>

        <div className="flex flex-col gap-6 mt-12">
          {SECTIONS.map((s, i) => (
            <motion.section
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.05 * (i % 3) }}
              className="bg-[#13141B] border border-white/10 rounded-3xl p-7 sm:p-8"
              data-testid={`privacy-section-${i}`}
            >
              <h2 className="font-heading text-lg sm:text-xl font-bold text-white">{s.title}</h2>
              <p className="text-sm sm:text-base text-slate-400 leading-relaxed mt-3">{s.body}</p>
            </motion.section>
          ))}
        </div>

        <p className="font-mono-adj text-[10px] uppercase tracking-[0.25em] text-slate-600 mt-12 text-center" data-testid="privacy-legal-footer">
          {COMPANY.legal} — {COMPANY.address} — P.IVA {COMPANY.piva}
        </p>
      </main>
    </div>
  );
}
